import { useEffect, useRef, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import LoadingButton from "@mui/lab/LoadingButton";
import VideoPlayer from "@components/ui/VideoPlayer";
import VideoThumbnail from "@components/ui/VideoThumbnail";
import AccessCodeWarning from "@components/ui/AccessCodeWarning";
import { getVideoList, submitVideoAnswer } from "@api/workshopVideoApi";
import { parseEventAccessCode } from "@hooks/useLocalStorage";
import "@styles/pages/WorkshopVideo/WorkshopVideo.scss";

const WorkshopVideoLanding = () => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const [data, setData] = useState(null);
    const [videos, setVideos] = useState([]);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [countDown, setCountdown] = useState(0);
    const [openQuestion, setOpenQuestion] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [answer, setAnswer] = useState("");
    const [answerError, setAnswerError] = useState(false);
    const isTimerStarted = useRef(false);

    useEffect(() => {
        const fetchVideo = async (code) => {
            try {
                const response = await getVideoList({ id, access_code: code });
                if (response && response.data && response.data.StatusCode === 200) {
                    setData(response.data.Data);
                    const videoList = response.data.Data.lst_video || [];
                    setVideos(videoList);
                }
            } catch (error) {
                console.error("Error fetching video list:", error);
            }
        };

        const code = parseEventAccessCode(id) || "";
        if (code) fetchVideo(code);
    }, []);

    useEffect(() => {
        let intervalId;
        const isLastVideo = videos.length > 0 && videos.length === currentVideoIndex + 1;

        if (isLastVideo && data?.isSubmit === 0 && data?.show_question === 1) {
            if (!isTimerStarted.current) {
                isTimerStarted.current = true;
                setCountdown(data.displayQuestionMin * 60);
                //setCountdown(1);
            }

            intervalId = setInterval(() => {
                setCountdown((prevCount) => {
                    if (prevCount <= 1) {
                        clearInterval(intervalId);
                        setOpenQuestion(true);
                        return 0;
                    }
                    return prevCount - 1;
                });
            }, 1000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [videos, currentVideoIndex, data]);

    const handleSubmit = async () => {
        if (answer !== '' && answer.length >= 20) {
            try {
                setSubmitLoading(true);
                const payload = {
                    enrollment_id: data.enrollment_id,
                    answer
                };
                const response = await submitVideoAnswer(payload);
                if (response && response.data && response.data.StatusCode === 200) {
                    setOpenQuestion(false);
                }
                setSubmitLoading(false);
            } catch (error) {
                console.error("Error submitting answer:", error);
                setSubmitLoading(false);
            }
        } else {
            setAnswerError(t("form:answer_error_empty"));
        }
    };

    return (
        <Box className="workshop-video-container">
            {data ? (
                <Box className="workshop-content">
                    <Typography className="title">
                        {data ? data[`title_${i18n.language}`] : ""}
                    </Typography>
                    
                    <Box className="video-container">
                        {videos.length > 0 && (
                            <VideoPlayer videoUrl={videos[currentVideoIndex]?.[`video_${i18n.language}`]} />
                        )}
                    </Box>

                    <Box className="description">
                        {data[`desc_${i18n.language}`]}
                    </Box>

                    {/* 直接在影片與簡介下方渲染問答區塊，完全不使用 Drawer 浮層 */}
                    {openQuestion && (
                        <Box className="question-drawer-content" sx={{ my: 4, p: 2, borderTop: "1px solid #E0E0E0" }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                {data?.[`question_${i18n.language}`]}
                            </Typography>

                            <TextField
                                name="answer"
                                fullWidth
                                multiline
                                rows={3}
                                onChange={(e) => {
                                    setAnswer(e.target.value);
                                    setAnswerError(false);
                                }}
                                variant="outlined"
                                sx={{ my: 2 }}
                                error={!!answerError}
                                helperText={!!answerError ? answerError : t("form:answer_helper_text")}
                            />

                            <Box className="next-button-container">
                                <LoadingButton
                                    className="loading-button"
                                    onClick={handleSubmit}
                                    loading={submitLoading}
                                >
                                    <Trans i18nKey="form:submit_button" />
                                </LoadingButton>
                            </Box>
                        </Box>
                    )}

                    {videos.length > 1 && (
                        <Box className="related-section">
                            <Typography className="related-title">
                                <Trans i18nKey="form:video_related_workshop" />
                            </Typography>
                            <Box className="related-video-container">
                                {videos.map((video, index) => {
                                    if (index === currentVideoIndex) return null;

                                    return (
                                        <Box 
                                            key={video.id || index}
                                            className="custom-image-wrapper"
                                            onClick={() => setCurrentVideoIndex(index)}
                                        >
                                            <Box className="card-image-container">
                                                <Box>
                                                    <Box className="video-player-icon-container">
                                                        <PlayArrowRoundedIcon className="video-player-icon" />
                                                    </Box>
                                                    <VideoThumbnail videoUrl={video[`video_${i18n.language}`]} seekTime={10} className="custom-image" />
                                                </Box>
                                                <Box className="title-button-container">
                                                    <Typography className="detail-title">
                                                        {data[`title_${i18n.language}`]}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Box>
            ) : null}
            <AccessCodeWarning workShopName={data ? data[`title_${i18n.language}`] : ""} />
        </Box>
    );
};

export default WorkshopVideoLanding;